import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { DSSetup, RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type AlertRecord from 'webapp/models/alert-setting';

export default class AlertSettingSerializer extends ApplicationSerializer {
	override getReadOnlyFields(
		_json: Record<string, unknown>,
	): RecordData.AlertSetting.ReadOnlyKey[] {
		return ['createdById', 'created'];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.AlertSetting.Raw | RecordData.AlertSetting.Serialized,
		prop: string | undefined,
	) {
		const { delivery, settings, ...data } = json;
		const attributes: Partial<RecordAttributes<AlertRecord>> & { id?: string | number } = omit(
			data as Omit<RecordData.AlertSetting.Raw, 'delivery' | 'settings'>,
			'createdBy',
		);

		if ('createdBy' in data && data.createdBy) {
			attributes.createdById = data.createdBy.id;
		}
		if (delivery) {
			attributes.inactive = delivery.inactive;
			attributes.deliveryTime = delivery.time;
			attributes.deliveryType = delivery.type;
			attributes.slackChannels = delivery.slackChannels;
		}
		if (settings) {
			if ('id' in settings) {
				attributes.metricId = settings.id;
			}

			if (settings.metricSetup) {
				const normalizedMetricSetup = this.normalizeMetricSetupData(
					settings.metricSetup as DSSetup.MetricSetupData,
				)!;
				attributes.metricSetupKey = normalizedMetricSetup.key;
				attributes.metricSetupSpaceAccessId = normalizedMetricSetup.spaceAccessId;
				attributes.metricSetupDimensions = normalizedMetricSetup.dimensions || [];
				attributes.metricSetupFilters = normalizedMetricSetup.filters || null;
			}
			attributes.dateRange = settings.dateRange;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.AlertSetting.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<AlertRecord>,
			RecordData.AlertSetting.ReadOnlyKey
		>;

		return {
			...omit(
				attributes,
				'deliveryTime',
				'deliveryType',
				'slackChannels',
				'inactive',
				'metricId',
				'metricSetupKey',
				'metricSetupSpaceAccessId',
				'metricSetupDimensions',
				'metricSetupFilters',
				'dateRange',
			),
			delivery: {
				time: attributes.deliveryTime,
				type: attributes.deliveryType,
				slackChannels: attributes.slackChannels,
				inactive: attributes.inactive,
			},
			settings: {
				id: attributes.metricId,
				metricSetup: this.serializeMetricSetupData({
					key: attributes.metricSetupKey,
					spaceAccessId: attributes.metricSetupSpaceAccessId,
					dimensions: attributes.metricSetupDimensions,
					filters: attributes.metricSetupFilters,
				}),
				dateRange: attributes.dateRange,
			},
		};
	}
}
